import { Injectable, inject } from "@angular/core";
import { collection, Firestore, getDocs, where, query } from "@angular/fire/firestore";
import { UserRole } from "../models/permissions.model";
import { Auth } from "@angular/fire/auth";

interface PermissionCheck {
	hasAccess: boolean;
	role?: UserRole;
	roles?: UserRole[];
	data?: any;
}

@Injectable({
	providedIn: "root",
})
export class PermissionsService {
	private firestore = inject(Firestore);
	private auth = inject(Auth);
	permissions = UserRole;

	constructor() {}

	// Get GLOBAL permissions from globalAdmins collection where logged in user matches userId field
	async getGlobalPermissions(userId: string): Promise<PermissionCheck> {
		// globalAdmins is admin-only read per the rules; a non-admin query
		// gets PERMISSION_DENIED. Treat denial as "not a global admin."
		try {
			const globalAdminsCollection = collection(this.firestore, "globalAdmins");
			const q = query(
				globalAdminsCollection,
				where("active", "==", true),
				where("user_id", "==", userId),
			);
			const snapshot = await getDocs(q);
			if (snapshot.empty) {
				return { hasAccess: false };
			}

			const doc = snapshot.docs[0];
			return {
				hasAccess: true,
				role: UserRole.ADMIN,
				data: doc.data(),
			};
		} catch {
			return { hasAccess: false };
		}
	}

	// Get organization permissions with role check
	async getOrganizationPermissions(
		organizationId: string,
		userId: string,
	): Promise<PermissionCheck> {
		const organizationMembersCollection = collection(this.firestore, "organizationMembers");
		const q = query(
			organizationMembersCollection,
			where("organization_id", "==", organizationId),
			where("user_id", "==", userId),
			where("active", "==", true),
		);
		const snapshot = await getDocs(q);

		if (snapshot.empty) {
			return { hasAccess: false };
		}

		const doc = snapshot.docs[0];
		const data = doc.data();
		const perms = data["roles"];
		return {
			hasAccess: true,
			role: data["roles"] || UserRole.ASSESSEE,
			roles: Object.keys(perms)
				.map((key) => (perms[key] ? (key as UserRole) : null))
				.filter((perm) => perm !== null),
			data,
		};
	}

	// Check if user has access to any organization
	async hasSomeOrganizationAccess(userId: string): Promise<PermissionCheck> {
		const organizationMembersCollection = collection(this.firestore, "organizationMembers");
		const q = query(organizationMembersCollection, where("user_id", "==", userId));
		const snapshot = await getDocs(q);

		if (snapshot.empty) {
			return { hasAccess: false };
		}

		const doc = snapshot.docs[0];
		const data = doc.data();
		return {
			hasAccess: true,
			role: data["role"] || UserRole.ASSESSEE,
			data,
		};
	}

	// Check if user has access to any partner
	async hasSomePartnerAccess(userId: string): Promise<PermissionCheck> {
		const partnerUsersCollection = collection(this.firestore, "partnerUsers");
		const q = query(partnerUsersCollection, where("user_id", "==", userId));
		const snapshot = await getDocs(q);

		if (snapshot.empty) {
			return { hasAccess: false };
		}

		const doc = snapshot.docs[0];
		const data = doc.data();
		return {
			hasAccess: true,
			role: data["role"] || UserRole.PARTNER_ADMIN,
			data,
		};
	}

	// Check if user has any type of access (global, organization, or partner)
	async hasAnyAccess(userId: string): Promise<PermissionCheck> {
		const [globalResult, orgResult, partnerResult] = await Promise.all([
			this.getGlobalPermissions(userId),
			this.hasSomeOrganizationAccess(userId),
			this.hasSomePartnerAccess(userId),
		]);

		if (globalResult.hasAccess) {
			return globalResult;
		}

		if (orgResult.hasAccess) {
			return orgResult;
		}

		if (partnerResult.hasAccess) {
			return partnerResult;
		}

		return { hasAccess: false };
	}

	// Get all organizations user has access to
	async getAllOrganizationAccess(userId: string): Promise<PermissionCheck[]> {
		const organizationMembersCollection = collection(this.firestore, "organizationMembers");
		const q = query(organizationMembersCollection, where("user_id", "==", userId));
		const snapshot = await getDocs(q);

		return snapshot.docs.map((doc) => {
			const data = doc.data();
			const perms = data["roles"];
			return {
				hasAccess: true,
				role: data["role"] || UserRole.ASSESSEE,
				roles: Object.keys(perms)
					.map((key) => (perms[key] ? (key as UserRole) : null))
					.filter((perm) => perm !== null),
				data: { ...data, id: doc.id },
			};
		});
	}

	// Get all partners user has access to
	async getAllPartnerAccess(userId: string): Promise<PermissionCheck[]> {
		const partnerUsersCollection = collection(this.firestore, "partnerUsers");
		const q = query(
			partnerUsersCollection,
			where("user_id", "==", userId),
			where("active", "==", true),
		);
		const snapshot = await getDocs(q);

		return snapshot.docs.map((doc) => {
			const data = doc.data();
			const roles = data["roles"] || {};
			// Check for either partner_admin or coach role
			const hasAccess = roles["partner_admin"] || roles["coach"];
			return {
				hasAccess: hasAccess,
				role: roles["partner_admin"] ? UserRole.PARTNER_ADMIN : UserRole.COACH,
				roles: Object.keys(roles)
					.map((key) => (roles[key] ? (key as UserRole) : null))
					.filter((perm) => perm !== null),
				data: { ...data, id: doc.id },
			};
		});
	}

	// Get partner permissions with role check
	async getPartnerPermissions(partnerId: string, userId: string): Promise<PermissionCheck> {
		const partnerUsersCollection = collection(this.firestore, "partnerUsers");
		const q = query(
			partnerUsersCollection,
			where("partner_id", "==", partnerId),
			where("user_id", "==", userId),
		);
		const snapshot = await getDocs(q);

		if (snapshot.empty) {
			return { hasAccess: false };
		}

		const doc = snapshot.docs[0];
		const data = doc.data();
		const roles = data["roles"] || {};
		// Check for either partner_admin or coach role
		const hasAccess = roles["partner_admin"] || roles["coach"];
		return {
			hasAccess: hasAccess,
			role: roles["partner_admin"] ? UserRole.PARTNER_ADMIN : UserRole.COACH,
			roles: Object.keys(roles)
				.map((key) => (roles[key] ? (key as UserRole) : null))
				.filter((perm) => perm !== null),
			data: { ...data, id: doc.id },
		};
	}

	// Check if user has specific role in organization
	async hasOrganizationRole(
		organizationId: string,
		userId: string,
		role: UserRole,
	): Promise<boolean> {
		const result = await this.getOrganizationPermissions(organizationId, userId);
		return result.hasAccess && result.role === role;
	}

	// Check if user has specific role in partner
	async hasPartnerRole(partnerId: string, userId: string, role: UserRole): Promise<boolean> {
		const result = await this.getPartnerPermissions(partnerId, userId);
		return result.hasAccess && result.role === role;
	}

	// Check if user is global admin (post-ZAN-291: existence of an active globalAdmins doc)
	async isGlobalAdmin(userId: string): Promise<boolean> {
		const result = await this.getGlobalPermissions(userId);
		return result.hasAccess;
	}

	// Get user-friendly message for permission errors
	getPermissionErrorMessage(
		context: "global" | "organization" | "partner" | "partners" | "organizations",
	): string {
		switch (context) {
			case "global":
				return "You don't have the correct permissions to access this page.";
			case "organization":
				return "You don't have permission to edit this organization.";
			case "organizations":
				return "You don't have permission to edit any organizations.";
			case "partner":
				return "You don't have permission to edit this partner.";
			case "partners":
				return "You don't have permission to edit any partners.";
			default:
				return "You don't have sufficient permissions to access this page.";
		}
	}
}
